import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import parse from 'html-react-parser'
import {
	ChevronLeft,
	Navigation,
	Loader2,
	StickyNote,
	Plus,
	GitBranch,
	ChevronRight,
	ListTodo,
	User,
	Trash2,
	MessageCircle,
	ListChecks,
} from 'lucide-react'
import { cn } from 'lib/utils'
import { fetchSiteById } from 'slices/evaluationSiteSlice'
import { getSiteNotes } from 'slices/siteNoteSlice'
import { getSiteImages } from 'slices/siteImageSlice'
import { getLocationByID } from 'slices/locationSlice'
import { initializeMyProjects } from 'slices/projectSlice'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { markCaptureImagesUsed } from 'slices/captureImageSlice'
import { type LibraryImage } from 'utils/libraryImages'
import { getTaskCNsByProject } from 'slices/taskCNSlice'
import { initializeNotes, deleteNote } from 'slices/locationNoteSlice'
import { getImages } from 'slices/locationImageSlice'
import FieldShell from '../components/FieldShell'
import FieldNoteComposer, { type ComposerPhoto } from '../components/FieldNoteComposer'
import FieldTaskComposer from '../components/FieldTaskComposer'
import FieldTaskEditSheet from '../components/FieldTaskEditSheet'
import FieldAvatar from '../components/FieldAvatar'
import NoteImageCarousel from '../components/NoteImageCarousel'
import ImageLibraryPicker from 'components/ImageLibraryPicker/ImageLibraryPicker'
import DeleteNoteDialog from 'components/NotesWorkspace/DeleteNoteDialog'
import { extractNoteImageUrls, splitNoteContent } from 'utils/noteImages'
import { createCapture, type Capture, type PhotoResult } from '../capture/captureTypes'
import { submitCapture } from '../capture/submitCapture'
import { createFieldHandlers } from '../capture/handlers'
import { appendImagesToNoteHtml } from '../utils/noteHtml'
import { directionsUrl } from '../utils/directions'
import { hasCoords } from '../utils/distance'
import { useChatPopup } from 'features/Chat/ChatPopupContext'
import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { NoteTasksDialog } from 'features/AskMdt/components/NoteTasksDialog'
import { fieldRoutes } from 'constants/fieldRoutes'
import { generatePath } from 'react-router-dom'

type ProjectTab = 'tasks' | 'notes'

/** Tasks first: it is what a project is FOR, and notes are the record of it. */
const PROJECT_TABS: { key: ProjectTab; label: string; icon: typeof ListTodo }[] = [
	{ key: 'tasks', label: 'Tasks', icon: ListTodo },
	{ key: 'notes', label: 'Notes', icon: StickyNote },
]

/** A photo the user has attached but not yet committed to a note. */
interface PendingPhoto extends ComposerPhoto {
	capture: Capture
}

export type FieldRecordKind = 'site' | 'location' | 'project'

/**
 * One record, in the field: what it is, how to drive to it, and its notes.
 *
 * **Serves both sites and locations**, because from here they are the same
 * screen — a name, a position, a note composer and a photo pipeline. Two copies
 * would drift, and the half that drifted would be the one nobody demoed. They
 * differ only in which collection the writes land in, and that difference lives
 * in the capture handlers rather than here.
 *
 * Locations matter more than sites for this screen, despite sites coming first:
 * they are much the bigger collection, and the case that asked for this was
 * catching up on one from an airport — nowhere near it, laptop in the bag.
 *
 * Deliberately thin. The full site card — cover photo, photo grid by category,
 * scores — is Phase 2.
 */
const FieldRecord = ({ kind }: { kind: FieldRecordKind }) => {
	const params = useParams()
	const recordId =
		(kind === 'site' ? params.siteId : kind === 'location' ? params.locationId : params.projectId) ||
		''
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const isProject = kind === 'project'
	/** Which half of a project is on screen. Only a project has two. */
	const [projectTab, setProjectTab] = useState<ProjectTab>('tasks')
	const chat = useChatPopup()
	/** The task whose edit sheet is open, or null. */
	const [editingTask, setEditingTask] = useState<any | null>(null)
	/** Open while choosing already-captured images for this note. */
	const [pickingImages, setPickingImages] = useState(false)

	const token = useSelector((state: any) => state.userLogin?.userInfo?.token)
	const userId = useSelector((state: any) => state.userLogin?.userInfo?._id)
	// "Make tasks" from a dictated note (MYD-643), where Ask MDT is offered.
	// Location and project notes only: a site has no project to put tasks on.
	const askAvailable = isAskMdtAvailable(useSelector((state: any) => state.userLogin?.userInfo))
	const [tasksFor, setTasksFor] = useState<string | null>(null)

	const site = useSelector((state: any) => state.evaluationSites.entities[recordId])
	const location = useSelector((state: any) => state.locations.entities?.[recordId])
	const project = useSelector((state: any) => state.projects?.entities?.[recordId])
	const record = kind === 'site' ? site : kind === 'location' ? location : project

	/**
	 * A project's notes and images live in the LOCATION collections, filed under
	 * the project. There is no project image API at all — projectImageModel
	 * exists with no routes and is not registered in server.js — and chat already
	 * works this way for the same reason. `projectAsset` is the location.
	 */
	const projectLocationId = kind === 'project' ? project?.projectAsset : undefined

	const siteNotes = useSelector((state: any) => state.siteNotes)
	const locationNotes = useSelector((state: any) => state.locationNotes)

	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [composing, setComposing] = useState(false)
	const [photos, setPhotos] = useState<PendingPhoto[]>([])
	/**
	 * Which record the loaded location notes belong to.
	 *
	 * `siteNotes` carries `loadedSiteId` for this; `locationNotes` has no
	 * equivalent, so without tracking it here the previous location's notes
	 * appear under this one's name while the fetch is in flight.
	 */
	const [notesLoadedFor, setNotesLoadedFor] = useState<string | null>(null)
	const [addingTask, setAddingTask] = useState(false)
	const [deleting, setDeleting] = useState<any>(null)
	const [deleteBusy, setDeleteBusy] = useState(false)
	const [deleteError, setDeleteError] = useState<string | null>(null)

	useEffect(() => {
		if (!recordId || record) return
		// getLocationByID's parameter is untyped, so TS infers `void` for it.
		if (kind === 'site') (dispatch as any)(fetchSiteById(recordId))
		else if (kind === 'location') (dispatch as any)((getLocationByID as any)(recordId))
		// No project get-by-id endpoint exists, so the list is the only way in.
		// It is bounded by what the operator owns, so this is cheap enough.
		else (dispatch as any)(initializeMyProjects())
	}, [dispatch, kind, recordId, record])

	/*
	 * A location's projects.
	 *
	 * `initializeMyProjects` rather than `initializeProjects({ locationId })`,
	 * even though the latter asks the server exactly this question: its reducer
	 * REPLACES state.ids with only that location's projects, which would clobber
	 * the list every other consumer reads. Fetching the whole set and filtering
	 * costs one request the app usually already has, and stomps nothing.
	 */
	useEffect(() => {
		if (kind !== 'location' || !recordId) return
		;(dispatch as any)(initializeMyProjects())
	}, [dispatch, kind, recordId])

	// The task edit sheet offers an assignee, which needs the partner list.
	// Only a project has tasks, so only a project pays for this.
	useEffect(() => {
		if (kind !== 'project') return
		;(dispatch as any)(initializeMyPartners())
	}, [dispatch, kind])

	useEffect(() => {
		if (!recordId) return
		if (kind === 'site') {
			if (siteNotes.loadedSiteId !== recordId) (dispatch as any)(getSiteNotes(recordId))
			return
		}
		// A project's notes cannot be fetched until its location is known.
		if (kind === 'project' && !projectLocationId) return
		if (notesLoadedFor === recordId) return
		setNotesLoadedFor(recordId)
		;(dispatch as any)(
			initializeNotes(
				kind === 'project'
					? { locationId: projectLocationId, projectId: recordId }
					: { locationId: recordId },
			),
		)
	}, [dispatch, kind, recordId, projectLocationId, siteNotes.loadedSiteId, notesLoadedFor])

	const projectState = useSelector((state: any) => state.projects)

	const locationProjects = useMemo(() => {
		if (kind !== 'location') return []
		return (projectState.ids || [])
			.map((id: string) => projectState.entities[id])
			.filter((project: any) => {
				if (!project || project.isDeleted) return false
				// projectAsset arrives populated or as a bare id depending on the
				// endpoint, so both are handled — the same rule taskTarget applies.
				const asset = project.projectAsset
				return String(typeof asset === 'string' ? asset : asset?._id) === recordId
			})
	}, [kind, recordId, projectState])

	const taskState = useSelector((state: any) => state.taskCNs)

	useEffect(() => {
		if (kind !== 'project' || !recordId) return
		;(dispatch as any)(getTaskCNsByProject({ projectid: recordId, userid: userId }))
	}, [dispatch, kind, recordId, userId])

	const tasks = useMemo(() => {
		if (kind !== 'project') return []
		return (taskState.ids || [])
			.map((id: string) => taskState.entities[id])
			.filter((task: any) => {
				if (!task || task.isDeleted || task.completed) return false
				// The store is keyed across every project, so this filters rather
				// than trusting the last fetch — same reason the notes list does.
				const id = task.projectid?._id ?? task.projectid
				return String(id) === recordId
			})
	}, [kind, recordId, taskState])

	const refreshTasks = useCallback(() => {
		if (recordId) (dispatch as any)(getTaskCNsByProject({ projectid: recordId, userid: userId }))
	}, [dispatch, recordId, userId])

	// Object URLs are held by the browser until revoked, and a visit's worth of
	// full-resolution photos is enough to matter on a phone. Unmount only —
	// revoking on every change would pull the preview out from under a
	// thumbnail still on screen.
	useEffect(
		() => () => {
			photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	const notes = useMemo(() => {
		if (kind === 'site') {
			return siteNotes.loadedSiteId === recordId
				? siteNotes.ids.map((id: string) => siteNotes.entities[id])
				: []
		}
		if (notesLoadedFor !== recordId) return []
		// Location notes are a flat collection across every location and project,
		// so this has to filter rather than trust the last fetch. Top-level notes
		// only — replies render under their parent on the desktop feed.
		return (locationNotes.ids || [])
			.map((id: string) => locationNotes.entities[id])
			.filter((note: any) => {
				if (!note || note.parentid) return false
				return kind === 'project'
					? String(note.projectid) === recordId
					: String(note.locationid) === recordId
			})
	}, [kind, recordId, siteNotes, locationNotes, notesLoadedFor])

	const notesLoading = kind === 'site' ? siteNotes.isLoading : locationNotes.isLoading

	/** The ids a capture carries, which is the only thing that differs. */
	const target = useMemo(() => {
		if (kind === 'site') return { siteId: recordId }
		if (kind === 'location') return { locationId: recordId }
		// Both, for a project: the row is filed against the location and tagged
		// with the project, which is the only arrangement the API supports.
		return { locationId: projectLocationId, projectId: recordId }
	}, [kind, recordId, projectLocationId])

	/**
	 * Upload one photo, touching only its own thumbnail.
	 *
	 * The capture is passed in rather than looked up out of state. A
	 * `setPhotos(prev => …)` updater does NOT run synchronously, so reading the
	 * list from inside one gives you nothing: the upload silently never starts
	 * and the thumbnail spins for ever.
	 */
	const uploadPhoto = useCallback(
		async (id: string, capture: Capture) => {
			setPhotos(prev =>
				prev.map(photo =>
					photo.id === id ? { ...photo, status: 'uploading', error: undefined } : photo,
				),
			)

			const sent = await submitCapture(capture, createFieldHandlers(token, userId))

			setPhotos(prev =>
				prev.map(photo =>
					photo.id === id
						? {
								...photo,
								capture: sent,
								status: sent.status === 'sent' ? 'uploaded' : 'failed',
								error: sent.error,
							}
						: photo,
				),
			)
		},
		[token, userId],
	)

	const addPhotos = useCallback(
		(files: File[]) => {
			const added: PendingPhoto[] = files.map(file => {
				const capture = createCapture('photo', { file, ...target })
				return {
					id: capture.id,
					previewUrl: URL.createObjectURL(file),
					status: 'ready' as const,
					capture,
				}
			})
			setPhotos(prev => [...prev, ...added])

			// One at a time, on purpose: several concurrent uploads over a weak
			// cellular connection are slower than one after another, and far more
			// likely to fail together.
			added.reduce((chain, photo) => chain.then(() => uploadPhoto(photo.id, photo.capture)), Promise.resolve())
		},
		[target, uploadPhoto],
	)

	/**
	 * Attach images already in the library.
	 *
	 * The same queue as a fresh photograph, so status, retry and removal behave
	 * identically. The difference lives in the handler: a library image is
	 * already on Cloudinary, so it skips compression and upload and only the row
	 * write remains.
	 *
	 * They are marked used AFTER the attach is started rather than before. The
	 * library is a record of what has been filed, and stamping an image the user
	 * then removes from the draft would be a lie.
	 */
	const addLibraryImages = useCallback(
		(rows: LibraryImage[]) => {
			if (rows.length === 0) return

			const added: PendingPhoto[] = rows.map(row => {
				const capture = createCapture('photo', {
					libraryImage: {
						/*
							Only a capture has one. The picker now spans both
							collections (MYD-469), and sending a filed image's id here
							would have it posted to `/api/captureimages/used`, where it
							matches nothing: a silent no-op that looks like the mark
							simply failed.
						*/
						captureImageId: row.origin === 'capture' ? row.id : '',
						url: row.url,
						name: row.name,
						size: row.size,
						// Carried through, not re-derived. Every field on
						// `LibraryImageRef` is optional, so omitting these compiled
						// perfectly well and silently wrote rows with no Cloudinary
						// id and no format: the format then rendered them as blank
						// video players in the Library. See MYD-461 / MYD-462.
						uuid: row.uuid,
						type: row.type,
					},
					...target,
				})
				return {
					id: capture.id,
					// The Cloudinary URL IS the preview: there is no local file to
					// make an object URL from, and nothing to revoke later.
					previewUrl: row.url,
					status: 'ready' as const,
					capture,
				}
			})
			setPhotos(prev => [...prev, ...added])

			added.reduce(
				(chain, photo) => chain.then(() => uploadPhoto(photo.id, photo.capture)),
				Promise.resolve(),
			)
			/*
				Captures only. A filed image has no `usedAt` to set, and posting
				its id would match nothing on the capture endpoint: a silent
				no-op indistinguishable from the mark having failed.
			*/
			const captureIds = rows.filter(row => row.origin === 'capture').map(row => row.id)
			if (captureIds.length) (dispatch as any)(markCaptureImagesUsed(captureIds))
		},
		[target, uploadPhoto, dispatch],
	)

	/** Retry one photo. The capture comes from the rendered list, not an updater. */
	const retryPhoto = useCallback(
		(id: string) => {
			const photo = photos.find(candidate => candidate.id === id)
			if (photo) uploadPhoto(id, photo.capture)
		},
		[photos, uploadPhoto],
	)

	const removePhoto = useCallback((id: string) => {
		setPhotos(prev => {
			const going = prev.find(photo => photo.id === id)
			if (going) URL.revokeObjectURL(going.previewUrl)
			return prev.filter(photo => photo.id !== id)
		})
	}, [])

	const discardDraft = useCallback(() => {
		photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl))
		setPhotos([])
		setComposing(false)
		setError(null)
	}, [photos])

	/**
	 * Save the note, with whatever photos are attached.
	 *
	 * The URLs go into the note body here. That is the second half of the dual
	 * write — the image rows were created by the photo handler as each upload
	 * landed. Both halves are required: without the row the gallery is empty,
	 * without the tag the note reads blank.
	 */
	const saveNote = useCallback(
		async (html: string) => {
			if (photos.some(photo => photo.status === 'uploading')) {
				setError('Still uploading a photo — give it a moment.')
				return
			}

			// Saving now would write a note citing pictures that do not exist.
			const failed = photos.filter(photo => photo.status === 'failed')
			if (failed.length) {
				const them = failed.length === 1 ? 'it' : 'them'
				setError(
					`${failed.length} photo${failed.length === 1 ? '' : 's'} did not upload. Retry or remove ${them} first.`,
				)
				return
			}

			setSaving(true)
			setError(null)

			const urls = photos.map(photo => (photo.capture.result as PhotoResult | undefined)?.url)
			const note = await submitCapture(
				createCapture('note', { html: appendImagesToNoteHtml(html, urls), ...target }),
				createFieldHandlers(token, userId),
			)
			setSaving(false)

			if (note.status !== 'sent') {
				setError(note.error ?? 'Could not save this note.')
				return
			}

			photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl))
			setPhotos([])
			setComposing(false)
			// Both the note and its image rows are new; re-read rather than
			// hand-patching two slices into agreement with the server.
			if (kind === 'site') {
				;(dispatch as any)(getSiteNotes(recordId))
				;(dispatch as any)(getSiteImages(recordId))
			} else if (kind === 'project') {
				;(dispatch as any)(
					initializeNotes({ locationId: projectLocationId, projectId: recordId }),
				)
				;(dispatch as any)(getImages({ locationid: projectLocationId, projectid: recordId }))
			} else {
				;(dispatch as any)(initializeNotes({ locationId: recordId }))
				// getImages destructures LOWERCASE keys. Passing locationId sent no
				// params at all, which refetched every image the user owns.
				;(dispatch as any)(getImages({ locationid: recordId }))
			}
		},
		[dispatch, kind, recordId, projectLocationId, target, token, userId, photos],
	)

	/**
	 * Only the author or the asset owner may delete, so the control is hidden
	 * for anyone else rather than offered and refused. The server enforces it
	 * regardless — this only decides whether the button is worth showing.
	 */
	const canDelete = useCallback(
		(note: any) => {
			const author = String(note?.userid?._id ?? note?.userid ?? '')
			const owner = String(record?.owner?._id ?? record?.owner ?? '')
			return Boolean(userId) && (String(userId) === author || String(userId) === owner)
		},
		[record, userId],
	)

	const confirmDelete = useCallback(async () => {
		if (!deleting) return
		setDeleteBusy(true)
		setDeleteError(null)

		const action: any = await (dispatch as any)(deleteNote(deleting._id))
		setDeleteBusy(false)

		// A rejected thunk resolves, so this has to be checked — and the server's
		// message is the useful one: it says whether a comment is in the way.
		if (action?.meta?.requestStatus === 'rejected') {
			setDeleteError(String(action.payload ?? 'Could not delete this note.'))
			return
		}

		setDeleting(null)
	}, [deleting, dispatch])

	const title =
		kind === 'site' ? record?.name : kind === 'location' ? record?.assetName : record?.projectName
	const addressLine =
		kind === 'project'
			? record?.projectDescription || record?.projectType || ''
			: [record?.address, record?.city, kind === 'site' ? record?.state : record?.stateProvince]
					.filter(Boolean)
					.join(', ')

	return (
		<FieldShell
			title={title ?? (kind === 'site' ? 'Site' : kind === 'location' ? 'Location' : 'Project')}
			// The tab bar would sit between the composer and the keyboard, and be
			// buried by it anyway. Cancel is the way out while composing, so the
			// screen is still not a dead end.
			hideTabs={composing || addingTask}
			headerAction={
				/*
					A project's conversation, opened for THIS project.

					The popup is the only chat surface the field app has, and it is
					global by default on purpose. Naming the room here is what makes
					this the project's conversation rather than a DM, and the parent
					location goes with it so a photograph sent in the chat is filed
					in the library that actually exists.
				*/
				kind === 'project' ? (
					<button
						type="button"
						onClick={() =>
							chat.open({
								room: {
									projectId: recordId,
									locationId: projectLocationId,
									label: title || 'Project',
								},
							})
						}
						aria-label={`Chat about ${title || 'this project'}`}
						className="inline-flex items-center justify-center w-11 h-11 rounded-full text-primary shrink-0"
					>
						<MessageCircle className="w-5 h-5" />
					</button>
				) : hasCoords(record) ? (
					<a
						href={directionsUrl({
							latitude: record.latitude,
							longitude: record.longitude,
							label: title,
						})}
						aria-label={`Directions to ${title}`}
						className="inline-flex items-center justify-center w-11 h-11 rounded-full text-primary shrink-0"
					>
						<Navigation className="w-5 h-5" />
					</a>
				) : undefined
			}
		>
			<div className="flex min-h-full flex-col">
				<div className="flex-1 pb-6">
					<button
						type="button"
						onClick={() => navigate(kind === 'project' ? fieldRoutes.PROJECTS : fieldRoutes.NEAR)}
						className="flex items-center gap-1 px-4 py-3 text-sm text-primary"
					>
						<ChevronLeft className="w-4 h-4" />
						{kind === 'project' ? 'Projects' : 'Locations'}
					</button>

					{!record ? (
						<p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
							<Loader2 className="w-4 h-4 animate-spin" />
							Loading…
						</p>
					) : (
						<>
							<div className="px-4 pb-4">
								<p className="text-sm text-gray-600">
									{addressLine || (kind === 'project' ? 'No description' : 'No address')}
								</p>
								{hasCoords(record) && (
									<p className="mt-0.5 text-xs text-gray-400 tabular-nums">
										{record.latitude.toFixed(5)}, {record.longitude.toFixed(5)}
										{record.gpsAccuracy ? ` · ±${Math.round(record.gpsAccuracy)} m` : ''}
									</p>
								)}
							</div>

							{/*
								A project's two bodies of work, one tap apart.

								They were stacked, so reaching a note meant scrolling past
								every task, and the screen had two competing add controls: a
								small "+ Add" link in the Tasks header and a full-width
								button for notes. Tabs give it one primary action that means
								whatever is on screen.

								Hidden while a composer is open. Switching tabs mid-draft
								would throw the draft away, and Cancel is already the way
								out, so the question is better not asked.
							*/}
							{kind === 'project' && !composing && !addingTask && (
								<div
									role="tablist"
									aria-label="Project contents"
									className="grid grid-cols-2 border-t border-b border-gray-200 bg-white"
								>
									{PROJECT_TABS.map(tab => {
										const active = projectTab === tab.key
										const count = tab.key === 'tasks' ? tasks.length : notes.length
										return (
											<button
												key={tab.key}
												type="button"
												role="tab"
												aria-selected={active}
												onClick={() => setProjectTab(tab.key)}
												className={cn(
													'-mb-px inline-flex items-center justify-center gap-2 border-b-2 py-3 text-sm font-medium',
													active
														? 'border-primary text-primary'
														: 'border-transparent text-gray-500',
												)}
											>
												<tab.icon className="h-4 w-4" />
												{tab.label}
												{count > 0 && (
													<span
														className={cn(
															'rounded-full px-1.5 text-xs tabular-nums',
															active ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500',
														)}
													>
														{count}
													</span>
												)}
											</button>
										)
									})}
								</div>
							)}

							{kind === 'project' && projectTab === 'tasks' && (
								<div className="bg-white">
									{tasks.length === 0 ? (
										<p className="px-4 py-4 text-sm text-gray-500">Nothing open.</p>
									) : (
										<ul className="divide-y divide-gray-100">
											{tasks.map((task: any) => {
												// Whose task it is now, not who typed it. Same rule
												// and same row shape as the Tasks screen, so a task
												// does not look like two different things depending
												// on which screen found it.
												const person = task.assignedTo || task.userid
												const personName = person?.name || person?.firstName || ''

												return (
													<li key={task._id}>
														<button
															type="button"
															onClick={() => setEditingTask(task)}
															className="flex w-full items-center gap-3 px-4 py-3 text-left"
														>
															<FieldAvatar
																src={person?.image}
																name={personName}
																fallbackIcon={User}
																label={personName || 'Unassigned'}
																className="h-9 w-9"
															/>
															<div className="min-w-0 flex-1">
																<p className="truncate text-sm font-medium text-gray-900">
																	{task.taskName}
																</p>
																<p className="mt-0.5 truncate text-xs text-gray-500">
																	{[
																		task.dateDue
																			? new Date(task.dateDue).toLocaleDateString(undefined, {
																					day: 'numeric',
																					month: 'short',
																				})
																			: 'No due date',
																		task.taskPriority,
																	]
																		.filter(Boolean)
																		.join(' · ')}
																</p>
															</div>
														</button>
													</li>
												)
											})}
										</ul>
									)}
								</div>
							)}

							{kind === 'location' && locationProjects.length > 0 && (
								<div className="border-t border-gray-200 bg-white">
									<h2 className="px-4 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
										Projects
									</h2>
									<ul className="divide-y divide-gray-100">
										{locationProjects.map((project: any) => (
											<li key={project._id}>
												<button
													type="button"
													onClick={() =>
														navigate(
															generatePath(fieldRoutes.PROJECT, { projectId: project._id }),
														)
													}
													className="flex w-full items-center gap-3 px-4 py-3 text-left"
												>
													<GitBranch className="h-4 w-4 shrink-0 text-gray-400" />
													<span className="min-w-0 flex-1">
														<span className="block truncate text-sm font-medium text-primary">
															{project.projectName || 'Untitled project'}
														</span>
														{(project.projectType || project.projectDescription) && (
															<span className="block truncate text-xs text-gray-500">
																{project.projectType || project.projectDescription}
															</span>
														)}
													</span>
													<ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
												</button>
											</li>
										))}
									</ul>
								</div>
							)}

							{/*
								On a project the tab already says "Notes", so the heading
								would be saying it twice. Elsewhere it is the only label.

								Not rendered at all on the Tasks tab rather than hidden with
								a class: a hidden subtree still holds its images in memory
								and still re-parses every note body on each render of this
								screen.
							*/}
							{(!isProject || projectTab === 'notes') && (
							<div className={cn('bg-white', !isProject && 'border-t border-gray-200')}>
								{!isProject && (
									<h2 className="px-4 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
										Notes
									</h2>
								)}

								{notesLoading && notes.length === 0 ? (
									<p className="px-4 pb-4 text-sm text-gray-500">Loading notes…</p>
								) : notes.length === 0 ? (
									<p className="px-4 pb-4 text-sm text-gray-500">
										Nothing recorded here yet.
									</p>
								) : (
									<ul className="divide-y divide-gray-100">
										{notes.map((note: any) => (
											<li key={note._id} className="px-4 py-3">
												<FieldNote note={note.note} />
												<div className="mt-1 flex items-center justify-between gap-2">
													<p className="text-xs text-gray-400">
														{new Date(note.createdAt).toLocaleString()}
													</p>
													<div className="flex items-center gap-1">
												{kind !== 'site' && askAvailable && (
													<button
														type="button"
														onClick={() => setTasksFor(note._id)}
														aria-label="Make tasks from this note"
														className="inline-flex h-8 items-center gap-1 rounded-full px-2 text-xs font-medium text-primary hover:bg-primary/5"
													>
														<ListChecks className="h-4 w-4" /> Tasks
													</button>
												)}
												{kind !== 'site' && canDelete(note) && (
														<button
															type="button"
															onClick={() => {
																setDeleteError(null)
																setDeleting(note)
															}}
															aria-label="Delete this note"
															className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-300 hover:text-destructive"
														>
															<Trash2 className="h-4 w-4" />
														</button>
													)}
												</div>
												</div>
											</li>
										))}
									</ul>
								)}
							</div>
							)}
						</>
					)}
				</div>

				{addingTask ? (
					<FieldTaskComposer
						projectId={recordId}
						onDone={() => {
							setAddingTask(false)
							refreshTasks()
						}}
						onCancel={() => setAddingTask(false)}
						className="sticky bottom-0 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
					/>
				) : composing ? (
					<FieldNoteComposer
						onSubmit={saveNote}
						onCancel={discardDraft}
						busy={saving}
						error={error}
						photos={photos}
						onAddPhotos={addPhotos}
						onPickFromLibrary={() => setPickingImages(true)}
						onRemovePhoto={removePhoto}
						onRetryPhoto={retryPhoto}
						autoFocus
						// Pinned, so the composer and its Save button stay put while the
						// note list scrolls behind them.
						className="sticky bottom-0 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
					/>
				) : (
					record && (
						<div
							className="sticky bottom-0 border-t border-gray-200 bg-white p-3"
							style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
						>
							{/*
								One primary action that means whatever is on screen. On a
								project it follows the tab; everywhere else there is only
								one thing to add, so it always adds a note.
							*/}
							{isProject && projectTab === 'tasks' ? (
								<button
									type="button"
									onClick={() => setAddingTask(true)}
									className="w-full inline-flex items-center justify-center gap-2 min-h-[2.75rem] rounded-md bg-primary text-white text-sm font-medium"
								>
									<Plus className="w-4 h-4" />
									Add a task
								</button>
							) : (
								<button
									type="button"
									onClick={() => setComposing(true)}
									className="w-full inline-flex items-center justify-center gap-2 min-h-[2.75rem] rounded-md bg-primary text-white text-sm font-medium"
								>
									<StickyNote className="w-4 h-4" />
									Add a note
								</button>
							)}
						</div>
					)
				)}
			</div>
			<ImageLibraryPicker
				open={pickingImages}
				onClose={() => setPickingImages(false)}
				onConfirm={addLibraryImages}
			/>

			<FieldTaskEditSheet
				task={editingTask}
				onClose={() => setEditingTask(null)}
				onSaved={refreshTasks}
			/>

		<DeleteNoteDialog
				open={Boolean(deleting)}
				onClose={() => {
					setDeleting(null)
					setDeleteError(null)
				}}
				onConfirm={confirmDelete}
				imageCount={extractNoteImageUrls(deleting?.note).length}
				busy={deleteBusy}
				error={deleteError}
			/>

			{tasksFor && <NoteTasksDialog key={tasksFor} open noteId={tasksFor} onClose={() => setTasksFor(null)} />}
		</FieldShell>
	)
}

/**
 * One note's body: its words, then its pictures.
 *
 * The two are stored together in one HTML string, and rendering it as stored
 * puts every photograph inline at full width. The desktop card makes the same
 * split and lays the images out in a collage; a phone has one column, so the
 * axis to spend is horizontal instead.
 */
const FieldNote = ({ note }: { note?: string | null }) => {
	const { textHtml, imageUrls } = useMemo(() => splitNoteContent(note), [note])

	return (
		<>
			{textHtml.trim() && (
				// Same renderer the desktop note feed uses, so a note written here
				// reads identically at a desk.
				<div className="prose prose-sm max-w-none text-sm text-gray-800 [&_p]:my-1">
					{parse(textHtml)}
				</div>
			)}
			<NoteImageCarousel urls={imageUrls} className={textHtml.trim() ? 'mt-2' : ''} />
		</>
	)
}

export default FieldRecord
